import type { NativeSession } from "../native/session";
import { addPage, removePage, renumberPages } from "../native/pages";
export class NativeScreens {
  private list = document.createElement("div");
  private rows = new Map<string, HTMLElement>();
  private layers = document.createElement("div");
  private collapsed = new Set<string>();
  private name = document.createElement("input");
  private lastPage = "";
  constructor(
    private session: NativeSession,
    private transition: (action: () => void) => void,
    private changed: () => void,
    private message: (text: string, error?: boolean) => void,
  ) {
    this.list.id = "screens-list";
    this.list.className = "screen-strip";
    const scroll = document.querySelector(
      "#native-left-panel .native-panel-scroll",
    )!;
    this.layers.className = "layers-panel";
    for (const n of [...scroll.children]) this.layers.append(n);
    scroll.append(this.list, this.layers);
    const actions = document.querySelector(".native-file-controls")!;
    for (const id of ["native-project", "native-duplicate", "native-delete"])
      document.getElementById(id)!.hidden = true;
    for (const [id, title, icon, run] of [
      [
        "add-new-screen",
        "Добавить экран",
        "M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z",
        () => this.change(() => addPage(session.document, crypto.randomUUID())),
      ],
      [
        "duplicate-screen",
        "Дублировать текущий экран",
        "M7 7V3h14v14h-4v4H3V7h4zm2 0h8v8h2V5H9v2zm-4 2v10h10V9H5z",
        () =>
          this.change(() =>
            addPage(session.document, crypto.randomUUID(), true),
          ),
      ],
      [
        "delete-current-screen",
        "Удалить текущий экран",
        "M7 21c-1.1 0-2-.9-2-2V8h14v11c0 1.1-.9 2-2 2H7zM9 4h6l1 2h4v2H4V6h4l1-2z",
        () => {
          if (
            session.document.pages.length > 1 &&
            confirm("Удалить текущий экран?")
          )
            this.change(() =>
              removePage(session.document, session.document.activePageId),
            );
        },
      ],
    ] as const) {
      const b = document.createElement("button");
      b.id = id;
      b.title = b.ariaLabel = title;
      b.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${icon}"/></svg>`;
      b.onclick = run;
      actions.insertBefore(b, document.getElementById("native-settings"));
    }
    const row = document.createElement("label");
    row.className = "interactive-row";
    row.id = "native-screen-name-row";
    row.textContent = "Название экрана";
    this.name.id = "screen-inspector-name";
    this.name.placeholder = "Например: Задание 1";
    row.append(this.name);
    document.getElementById("native-page-properties")?.prepend(row);
    if (!row.isConnected)
      document
        .querySelector("#native-right-panel .native-panel-scroll")!
        .prepend(row);
    this.name.onchange = () => {
      const name = this.name.value.trim() || "Экран";
      this.transition(() => {
        session.changePages(
          {
            ...session.document,
            pages: session.document.pages.map((p) =>
              p.id === session.document.activePageId ? { ...p, name } : p,
            ),
          },
          true,
        );
        this.changed();
      });
    };
  }
  private change(action: () => ReturnType<typeof addPage>) {
    this.transition(() => {
      try {
        this.session.changePages(action(), true);
        this.changed();
        document.getElementById("native-page-open")!.click();
      } catch (e) {
        this.message((e as Error).message, true);
      }
    });
  }
  render() {
    const d = this.session.document;
    if (
      this.lastPage !== d.activePageId ||
      document.activeElement !== this.name
    ) {
      this.name.value = this.session.page.name;
      this.lastPage = d.activePageId;
    }
    (
      document.getElementById("delete-current-screen") as HTMLButtonElement
    ).disabled = d.pages.length <= 1;
    const present = new Set(d.pages.map((p) => p.id));
    for (const [id, row] of this.rows)
      if (!present.has(id)) {
        row.remove();
        this.rows.delete(id);
      }
    d.pages.forEach((p, index) => {
      let row = this.rows.get(p.id);
      if (!row) {
        row = document.createElement("div");
        row.className = "screen-item";
        row.dataset.screenId = p.id;
        row.tabIndex = 0;
        row.setAttribute("role", "button");
        row.className = "screen-accordion-item";
        row.innerHTML =
          '<div class="screen-item"><div class="screen-number"></div><div class="screen-name"></div><button type="button" class="screen-layers-toggle"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14l5-5 5 5z"/></svg></button></div>';
        row.querySelector<HTMLButtonElement>("button")!.onclick = (e) => {
          e.stopPropagation();
          if (this.collapsed.has(p.id)) this.collapsed.delete(p.id);
          else this.collapsed.add(p.id);
          this.changed();
        };
        row.onclick = (e) => {
          if (!(e.target as HTMLElement).closest(".screen-item")) return;
          this.transition(() => {
            if (this.session.document.activePageId !== p.id)
              this.session.changePages(
                { ...this.session.document, activePageId: p.id },
                false,
              );
            this.changed();
            document.getElementById("native-page-open")!.click();
          });
        };
        row.onkeydown = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            if (e.target !== row) return;
            e.preventDefault();
            row!.querySelector<HTMLElement>(".screen-item")!.click();
          }
        };
        row.querySelector<HTMLElement>(".screen-item")!.draggable = true;
        row.ondragstart = (e) => {
          if (!(e.target as HTMLElement).closest(".screen-item")) return;
          e.dataTransfer?.setData("application/x-creator-screen", p.id);
        };
        row.ondragover = (e) => e.preventDefault();
        row.ondrop = (e) => {
          e.preventDefault();
          const id = e.dataTransfer?.getData("application/x-creator-screen");
          if (!id || id === p.id) return;
          this.change(() => {
            const pages = this.session.document.pages.slice(),
              from = pages.findIndex((q) => q.id === id),
              to = pages.findIndex((q) => q.id === p.id);
            if (from < 0 || to < 0) return this.session.document;
            pages.splice(to, 0, pages.splice(from, 1)[0]!);
            return renumberPages({ ...this.session.document, pages });
          });
        };
        this.rows.set(p.id, row);
      }
      const active = p.id === d.activePageId;
      row.classList.toggle("active", active);
      row.classList.toggle("layers-collapsed", this.collapsed.has(p.id));
      const item = row.querySelector<HTMLElement>(".screen-item")!;
      item.classList.toggle("active", active);
      row.setAttribute("aria-pressed", String(active));
      row.title = p.name;
      row.querySelector(".screen-number")!.textContent = String(index + 1);
      row.querySelector(".screen-name")!.textContent = p.name;
      const toggle = row.querySelector<HTMLButtonElement>("button")!;
      toggle.hidden = !active;
      toggle.title = toggle.ariaLabel = this.collapsed.has(p.id)
        ? "Развернуть слои экрана"
        : "Свернуть слои экрана";
      toggle.setAttribute("aria-expanded", String(!this.collapsed.has(p.id)));
      if (this.list.children[index] !== row)
        this.list.insertBefore(row, this.list.children[index] || null);
      if (active) {
        if (this.layers.parentElement !== row) row.append(this.layers);
        this.layers.hidden = this.collapsed.has(p.id);
      }
    });
  }
}
